import { useCallback, useEffect, useMemo, useState } from 'react';
import { getOwnSubjects } from '../../Subject/services/subjectService';
import {
  applyReviews,
  getAttentionList,
  getAttentionReviews,
  markAddressed,
  undoAddressed,
} from '../services/attentionService';

// The students who need a teacher's attention, loaded separately from the KPI numbers so a slow or
// failed lookup here can't hold those back. `error` is set if it couldn't be loaded. `subjects` are
// the teacher's own subjects the list was built from. Each flag carries `review` once marked as
// addressed (applyReviews); `markAddressed(flag)` and `undoAddressed(review)` change that.
export function useAttentionList(enabled) {
  const [rawAttention, setRawAttention] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;

    const load = getOwnSubjects().then((ownSubjects) => Promise.all([ownSubjects, getAttentionList(ownSubjects)]));

    Promise.all([load, getAttentionReviews()])
      .then(([[listSubjects, result], ownReviews]) => {
        if (!mounted) return;
        setSubjects(listSubjects);
        setRawAttention(result);
        setReviews(ownReviews);
      })
      .catch((err) => {
        if (mounted) setError(err.message || 'Failed to load the attention list');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [enabled]);

  const attention = useMemo(() => applyReviews(rawAttention, reviews), [rawAttention, reviews]);

  const mark = useCallback(async (flag) => {
    const review = await markAddressed(flag);
    setReviews((current) => [
      ...current.filter(
        (r) => !(r.student_id === review.student_id && r.reason === review.reason && r.subject_id === review.subject_id)
      ),
      review,
    ]);
  }, []);

  const undo = useCallback(async (review) => {
    await undoAddressed(review.id);
    setReviews((current) => current.filter((r) => r.id !== review.id));
  }, []);

  return { attention, subjects, loading, error, markAddressed: mark, undoAddressed: undo };
}
